import { z } from "zod";
import type { DoseUnit } from "./catalog";
import type { HealthSummary } from "./forms";
import type { ChartLotOption } from "./inventory";

/**
 * Nurse charts (chunk C): IV Charting, IM Only Charting, Adverse Reaction Report.
 * Phase 1 ships VV's templates in code; a clinic chart editor comes in Phase 2.
 */

/** Where a catalog item appears on charts. Null on the item means "automatic". */
export const CHART_SECTIONS = ["cocktail", "addons", "medications", "vitamins", "recovery", "hidden"] as const;
export type ChartSection = (typeof CHART_SECTIONS)[number];
/** Sections that go on a nurse's chart; a visit with none of them (only recovery, or items not on charts) needs no chart. */
export const CHARTED_SECTIONS: readonly ChartSection[] = ["cocktail", "addons", "medications", "vitamins"];
/** Whether a visit's items need a chart (an item that isn't in the catalog list counts, to be safe). */
export const visitNeedsChart = (itemIds: string[], items: { id: string; effectiveChartSection: ChartSection }[]) =>
  !itemIds.length || itemIds.some((id) => { const i = items.find((x) => x.id === id); return !i || CHARTED_SECTIONS.includes(i.effectiveChartSection); });
export const CHART_SECTION_LABELS: Record<ChartSection, string> = {
  cocktail: "IV therapy cocktail (IV chart)",
  addons: "Add-ons (IV chart)",
  medications: "Medications (IV and IM charts)",
  vitamins: "Vitamins (IM chart)",
  recovery: "Recovery (IV and IM charts)",
  hidden: "Not on charts",
};

export type ChartField =
  /** Shown from the record (name, DOB, nurse, date, allergies, medications, consents). */
  | { kind: "record"; key: "client" | "visit" | "health" | "forms" }
  | { kind: "checks"; key: string; label: string; options: string[]; required?: boolean }
  | { kind: "choice"; key: string; label: string; options: string[]; other?: boolean; required?: boolean; default?: string }
  | { kind: "input"; key: string; label: string; multiline?: boolean; input?: "text" | "time" | "number"; required?: boolean; /** "AI Cleanup" under it (the provider narrative). */ aiCleanup?: boolean }
  | { kind: "vitals"; key: string; label: string }
  /** Items from the catalog, with lot number and expiration. */
  | { kind: "catalog"; key: string; label: string; sections: ChartSection[]; single?: boolean; /** true: only IM/SQ shots; false: no shots. */ shots?: boolean };

export interface ChartSectionDef {
  title: string;
  fields: ChartField[];
}

export interface ChartTemplate {
  key: "iv" | "im" | "adverse";
  name: string;
  sections: ChartSectionDef[];
}

export type ChartTemplateKey = ChartTemplate["key"];

const SUBJECTIVE: ChartField = {
  kind: "checks",
  key: "subjective",
  label: "Subjective",
  required: true,
  options: ["PMH and allergies reviewed prior to treatment", "Reviewed patient safety contraindications", "Client consent(s) reviewed and signed", "Completed telehealth call"],
};

export const CHART_TEMPLATES: Record<ChartTemplate["key"], ChartTemplate> = {
  iv: {
    key: "iv",
    name: "IV Charting",
    sections: [
      { title: "Client", fields: [{ kind: "record", key: "client" }, { kind: "record", key: "health" }, { kind: "record", key: "forms" }] },
      { title: "Subjective", fields: [SUBJECTIVE, { kind: "input", key: "subjectiveComments", label: "Comments", multiline: true }] },
      { title: "Pre-assessment", fields: [{ kind: "vitals", key: "preVitals", label: "Vitals before" }] },
      {
        title: "IV insertion",
        fields: [
          { kind: "choice", key: "aseptic", label: "Aseptic technique", options: ["Alcohol", "Chlorhexidine"], required: true },
          { kind: "choice", key: "side", label: "Side", options: ["Left", "Right"], other: true, required: true },
          { kind: "choice", key: "site", label: "Site", options: ["Hand", "A/C", "Forearm", "Wrist"], other: true, required: true },
          { kind: "choice", key: "ivSize", label: "IV size", options: ["24G", "22G", "20G", "N/A"], required: true },
          { kind: "choice", key: "fluid", label: "Type of fluid", options: ["Lactated Ringer's", "Normal Saline"], default: "Lactated Ringer's", required: true },
          { kind: "input", key: "volume", label: "Volume given (mL)", input: "number", required: true },
          { kind: "input", key: "timeIn", label: "Time of insertion", input: "time", required: true },
          { kind: "input", key: "attempts", label: "Attempts", input: "number", required: true },
        ],
      },
      {
        title: "Given",
        fields: [
          // A visit can have more than one drip. Everything given in the IV (add-ons, medications,
          // vitamins) is one list; shots (IM/SQ) are their own, as each is its own line on the bill.
          { kind: "catalog", key: "cocktail", label: "Drip", sections: ["cocktail"] },
          { kind: "catalog", key: "addons", label: "Added to the bag", sections: ["addons", "medications", "vitamins"], shots: false },
          { kind: "catalog", key: "injections", label: "Injections given", sections: ["medications", "vitamins"], shots: true },
          { kind: "catalog", key: "recovery", label: "Recovery", sections: ["recovery"] },
        ],
      },
      {
        title: "Post-IV",
        fields: [
          {
            kind: "checks",
            key: "post",
            label: "Post-IV",
            required: true,
            options: ["No adverse reactions noted", "Catheter intact on removal", "Cotton ball and Coban dressing applied", "Reassessment after 15 minutes", "Patient received post-IV instructions"],
          },
          // With the end of the visit (it was under IV insertion; the saved field is the same).
          { kind: "input", key: "timeOut", label: "Time removed", input: "time", required: true },
          { kind: "vitals", key: "postVitals", label: "Vitals after" },
          // The key stays "comments", so charts started before keep what was typed.
          { kind: "input", key: "comments", label: "Provider narrative", multiline: true, required: true, aiCleanup: true },
        ],
      },
    ],
  },
  im: {
    key: "im",
    name: "IM Only Charting",
    sections: [
      { title: "Client", fields: [{ kind: "record", key: "client" }, { kind: "record", key: "health" }, { kind: "record", key: "forms" }] },
      { title: "Subjective", fields: [SUBJECTIVE, { kind: "input", key: "subjectiveComments", label: "Comments", multiline: true }] },
      {
        title: "Pre-assessment",
        fields: [
          { kind: "checks", key: "preAssessment", label: "Pre-assessment", required: true, options: ["Alert and oriented x4", "Respirations even and unlabored", "Skin warm, dry and intact"] },
        ],
      },
      {
        title: "Injection",
        fields: [
          { kind: "choice", key: "aseptic", label: "Aseptic technique", options: ["Alcohol", "Chlorhexidine"], required: true },
          { kind: "choice", key: "site", label: "Site", options: ["Deltoid", "Thigh", "Glute", "Belly"], other: true, required: true },
          { kind: "choice", key: "route", label: "Route", options: ["IM", "SQ"], required: true },
          { kind: "choice", key: "needle", label: "Needle", options: ["25G", "22G", "18G", "N/A"], other: true, required: true },
          { kind: "choice", key: "side", label: "Side", options: ["Left", "Right"], other: true, required: true },
          { kind: "input", key: "volume", label: "Volume given (mL)", input: "number", required: true },
          { kind: "input", key: "timeGiven", label: "Time given", input: "time", required: true },
        ],
      },
      {
        title: "Given",
        fields: [
          { kind: "catalog", key: "medications", label: "Given", sections: ["medications", "vitamins"] },
          { kind: "catalog", key: "recovery", label: "Recovery", sections: ["recovery"] },
        ],
      },
      {
        title: "Post-injection",
        fields: [
          { kind: "checks", key: "post", label: "Post-injection", required: true, options: ["No adverse reactions noted"] },
          { kind: "input", key: "comments", label: "Provider narrative", multiline: true, required: true, aiCleanup: true },
        ],
      },
    ],
  },
  adverse: {
    key: "adverse",
    name: "Adverse Reaction Report",
    sections: [
      { title: "Client and visit", fields: [{ kind: "record", key: "client" }, { kind: "record", key: "visit" }, { kind: "input", key: "incidentTime", label: "Time of incident", input: "time", required: true }] },
      {
        title: "Service administered",
        fields: [
          { kind: "input", key: "product", label: "Product / formulation", required: true },
          { kind: "input", key: "lot", label: "Lot number" },
          { kind: "choice", key: "route", label: "Route", options: ["IV", "IM"], required: true },
          { kind: "input", key: "dose", label: "Dose / volume" },
          { kind: "input", key: "rate", label: "Infusion rate / duration" },
          { kind: "input", key: "site", label: "Administration site" },
        ],
      },
      {
        title: "Reaction",
        fields: [
          {
            kind: "checks",
            key: "symptoms",
            label: "Symptoms observed or reported",
            required: true,
            options: [
              "Local redness / erythema",
              "Swelling at site",
              "Pain or burning at site",
              "Bruising / hematoma",
              "Itching / pruritus",
              "Rash or hives (urticaria)",
              "Flushing / warmth",
              "Nausea or vomiting",
              "Dizziness / lightheadedness",
              "Shortness of breath",
              "Chest tightness or pain",
              "Hypotension (low BP)",
              "Tachycardia (rapid HR)",
              "Syncope / fainting",
              "Anaphylaxis / severe allergic reaction",
            ],
          },
          { kind: "input", key: "symptomsOther", label: "Other symptoms" },
          { kind: "input", key: "firstNoted", label: "Time reaction first noted", input: "time", required: true },
          { kind: "input", key: "resolved", label: "Time resolved (24-hour, or “ongoing”)" },
          { kind: "choice", key: "severity", label: "Severity", required: true, options: ["Mild (localized, no intervention needed)", "Moderate (required intervention)", "Severe (emergency response activated)"] },
        ],
      },
      {
        title: "Interventions and actions",
        fields: [
          {
            kind: "checks",
            key: "interventions",
            label: "Select all that apply",
            options: [
              "IV / infusion stopped",
              "Rate slowed",
              "Cool compress applied",
              "Patient repositioned",
              "Vital signs monitored",
              "Oral hydration provided",
              "Diphenhydramine (Benadryl) administered",
              "Epinephrine administered",
              "EMS / 911 called",
              "Patient transported to ER",
              "Physician notified",
            ],
          },
          { kind: "input", key: "interventionsOther", label: "Other interventions" },
          { kind: "input", key: "detail", label: "Describe interventions and patient response in detail", multiline: true, required: true },
        ],
      },
      {
        title: "Outcome",
        fields: [
          { kind: "input", key: "condition", label: "Patient condition at end of visit / discharge", multiline: true, required: true },
          { kind: "input", key: "followUp", label: "Follow-up instructions given to patient", multiline: true },
          { kind: "choice", key: "referred", label: "Referred to physician / ER?", options: ["No", "Yes"], required: true },
          { kind: "input", key: "facility", label: "If yes, facility name" },
        ],
      },
    ],
  },
};

/** Normal adult ranges; outside them is highlighted, never blocked. */
export const VITAL_RANGES = {
  temp: { label: "Temp (°F)", min: 97, max: 100.4 },
  pulse: { label: "Pulse", min: 50, max: 110 },
  resp: { label: "Resp", min: 10, max: 24 },
  systolic: { label: "BP systolic", min: 90, max: 160 },
  diastolic: { label: "BP diastolic", min: 50, max: 100 },
  o2: { label: "O₂ sat (%)", min: 92, max: 100 },
} as const;
export type VitalKey = keyof typeof VITAL_RANGES;

/** A catalog line on a chart: what was given, with lot and expiration. */
export interface ChartItem {
  catalogItemId: string | null;
  name: string;
  /** For items without medications in their recipe (one vial, one lot). */
  lot: string;
  expiration: string;
  /** Added during the visit (not booked): goes on the bill. */
  added: boolean;
  /** Put on an updated chart because it was billed at checkout (already on the bill). */
  atCheckout?: boolean;
  /** Each medication (and bag of fluid) in the item's recipe, with the lot it was drawn from. */
  ingredients?: ChartIngredient[];
  /** Items with nothing to draw up (recovery): when the nurse marked it done, from the visit screen. */
  addedAt?: string | null;
}

/** One medication (or bag of fluid) of a charted item: its dose and the lot drawn from (picked from inventory, or typed). */
export interface ChartIngredient {
  formularyItemId: string;
  name: string;
  dose: number;
  unit: DoseUnit;
  /** An inventory lot; null when typed (a vial the app doesn't know). */
  lotId: string | null;
  lot: string;
  expiration: string;
  /** When the nurse marked it added to the bag (or drawn up), from the visit screen. */
  addedAt?: string | null;
  /** Left out of this visit (the client can't have it): not given, not taken off the shelf. Medications only. */
  leftOut?: boolean;
  /** Why it was left out ("Can't have calcium"); may be blank. */
  leftOutReason?: string;
}

export type ChartValue = string | string[] | Record<string, string> | ChartItem[] | null;
export type ChartData = Record<string, ChartValue>;

export const chartSaveSchema = z.object({
  data: z.record(z.string(), z.unknown()),
  /** The chart's updatedAt as the screen loaded it: a save over a change made by modifying the appointment is refused. */
  baseUpdatedAt: z.string().optional(),
});

export interface ChartOption {
  catalogItemId: string;
  name: string;
  section: ChartSection;
  /** Given as a shot (IM/SQ), not through the IV line. */
  shot?: boolean;
  booked: boolean;
  /** Medications and bags of fluid in its recipe (each gets its own lot on the chart). */
  ingredients: { formularyItemId: string; name: string; dose: number; unit: DoseUnit; strengthPerMl: number | null; fluid?: boolean }[];
}

/** Whether a chart's catalog list offers this item. */
export const fieldOffers = (f: Extract<ChartField, { kind: "catalog" }>, o: ChartOption) => f.sections.includes(o.section) && (f.shots === undefined || f.shots === !!o.shot);

export interface ChartView {
  id: string;
  appointmentId: string;
  clientId: string;
  templateKey: ChartTemplate["key"];
  template: ChartTemplate;
  status: ChartStatus;
  data: ChartData;
  /** Catalog choices offered on this chart (snapshot once signed). */
  options: ChartOption[];
  /** Lots on hand per medication, for picking (drafts only). */
  lots: Record<string, ChartLotOption[]>;
  /** Record-linked facts (snapshot once signed). */
  record: {
    client: { name: string; dob: string | null; age: number | null };
    visit: { date: string; start: string; nurse: string; services: string[] };
    health: HealthSummary;
    forms: { name: string; ok: boolean }[];
  };
  createdBy: string;
  updatedAt: string;
  signedBy: string | null;
  signedAt: string | null;
  /** The nurse's electronic signature (null for charts signed before these were kept). */
  signature: ESignature | null;
  addenda: { id: string; text: string; by: string; at: string }[];
  /** Medical director review: not needed, waiting, or done. */
  review: ChartReviewState;
  /** Fields still empty that the template marks required (before signing). */
  missing: string[];
  /** An updated chart: the signed chart it replaces, and why it was updated. */
  replaces: { id: string; signedBy: string; signedAt: string } | null;
  updateReason: string | null;
  /** The updated chart that replaces this one (signed), or is being written (draft). */
  replacedBy: { id: string; status: "draft" | "signed"; by: string; at: string } | null;
  /** Billed at checkout but not on this chart (the visit's current chart only). */
  notCharted: string[];
}

/** discarded: an updated chart started by mistake and put aside unsigned. */
export type ChartStatus = "draft" | "signed" | "discarded";

/** Starting an updated chart: why the signed one is being updated. */
export const chartUpdateSchema = z.object({ reason: z.string().trim().min(3, "Say why the chart is being updated.").max(1000) });

export interface ChartSummary {
  id: string;
  appointmentId: string;
  templateKey: ChartTemplate["key"];
  name: string;
  status: ChartStatus;
  date: string;
  serviceName: string;
  signedBy: string | null;
  signedAt: string | null;
  review: ChartReviewState;
  /** An updated chart (it replaced an earlier signed one). */
  isUpdate: boolean;
  /** Replaced by an updated chart, signed at this time (kept in the file, unchanged). */
  replacedAt: string | null;
}

export type ChartReviewState = { status: "not_needed" } | { status: "waiting" } | { status: "reviewed"; by: string; at: string; note: string; signature?: ESignature | null };

/**
 * An electronic signature on a chart or a review, kept as it was at that moment: the statement they
 * agreed to, their name and credentials, their saved signature (if any), and where it came from.
 * The hash covers the statement, the signer, the time, and what was signed, to show nothing changed.
 */
export interface ESignature {
  statement: string;
  name: string;
  credentials: string;
  /** Their saved signature (PNG data URL); null when they have none (their name stands for it). */
  image: string | null;
  at: string;
  ip: string | null;
  userAgent: string | null;
  hash: string;
}

/** What a nurse agrees to when signing a chart, and the medical director when reviewing one. */
export const SIGN_STATEMENTS = {
  chart: "I gave or oversaw the care recorded in this chart, and it is accurate and complete. I am signing it electronically, and this is my signature.",
  review: "I have reviewed this chart as the medical director. I am signing this review electronically, and this is my signature.",
} as const;

/**
 * A visit whose chart still has to be signed: a draft once the visit is completed or its day has
 * passed, or a completed visit with no chart started (chartId null).
 */
export interface ChartToSign {
  appointmentId: string;
  chartId: string | null;
  clientName: string;
  /** The chart's name, or "No chart started". */
  name: string;
  serviceName: string;
  date: string;
  /** Who has to sign it: whoever started the draft, else the visit's nurse. Null: nobody assigned. */
  staffId: string | null;
  staffName: string;
  /** Signed, but billed at checkout with items it doesn't have: these. Opens the signed chart to update it. */
  notCharted?: string[];
}

/** A chart waiting for the medical director (Pending Charts). */
export interface PendingReviewChart {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  serviceName: string;
  date: string;
  nurse: string;
  signedAt: string;
  /** Signed in the old system ("boulevard"): opens at /records/:id rather than /charts/:id. */
  source: string | null;
}

export const addendumSchema = z.object({ text: z.string().trim().min(1).max(4000) });

/** A chart time: 24-hour, "14:35". */
export const isChartTime = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);

/**
 * Charts made before the lists were merged (2026-10-25): the IV chart's "Medications given" now sits
 * in "Added to the bag", and the IM chart's "Vitamins" in "Given". Signed charts are never changed;
 * this is how they're read.
 */
export function mergedChartData(templateKey: string, data: ChartData): ChartData {
  const move = (from: string, to: string) => {
    const old = data[from];
    if (!Array.isArray(old) || !old.length || typeof old[0] !== "object") return data;
    const into = Array.isArray(data[to]) ? (data[to] as ChartItem[]) : [];
    const { [from]: _gone, ...rest } = data;
    return { ...rest, [to]: [...into, ...(old as ChartItem[])] };
  };
  return templateKey === "iv" ? move("medications", "addons") : templateKey === "im" ? move("vitamins", "medications") : data;
}

/** Which required fields are empty (or, for times, not a 24-hour time). */
export function missingFields(template: ChartTemplate, data: ChartData): string[] {
  const out: string[] = [];
  for (const s of template.sections) {
    for (const f of s.fields) {
      if (f.kind === "record" || f.kind === "catalog" || f.kind === "vitals") continue;
      const v = data[f.key];
      if (f.kind === "input" && f.input === "time" && typeof v === "string" && v !== "" && !isChartTime(v)) {
        out.push(`${f.label} (24-hour, like 14:35)`);
        continue;
      }
      if (!("required" in f) || !f.required) continue;
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) out.push(f.label);
    }
  }
  return out;
}

/** Pending Charts: each person's own to-do first; the badge counts only that. */
export interface PendingCharts {
  /** Charts this person has to sign. */
  mine: ChartToSign[];
  /** Everyone else's charts to sign (only with "See everyone's pending charts"). */
  others: ChartToSign[];
  /** Charts this person reviews as the medical director. */
  toReview: PendingReviewChart[];
  /** Signed charts waiting for the medical director: their own (nurses), or all (managers). */
  waiting: PendingReviewChart[];
  /** The menu badge: mine, plus toReview for the medical director. */
  count: number;
}

/**
 * AI Cleanup of a chart field: the text to tidy. With no text, a draft is written from what's filled
 * in on the chart (`data`, the screen's current entries).
 */
export const aiCleanupSchema = z.object({ field: z.string().max(40), text: z.string().trim().max(8000).default(""), data: z.record(z.string(), z.unknown()).optional() });

export interface AiCleanupResult {
  text: string;
  /** Written from the chart's entries (the narrative was empty), rather than tidied. */
  drafted: boolean;
  /** Numbers (doses, volumes, times) in the cleaned text that aren't in what was written: check before using it. */
  newNumbers: string[];
}

/**
 * What's filled in on a chart, as plain lines for the AI to draft a narrative from: the chart's own
 * fields only (never the client's name, date of birth, health history, or lots), skipping `except`
 * (the narrative itself) and anything empty.
 */
export function chartSummary(template: ChartTemplate, data: ChartData, except: string): string {
  const lines: string[] = [`Chart: ${template.name}`];
  for (const s of template.sections) {
    for (const f of s.fields) {
      if (f.kind === "record" || f.key === except) continue;
      const v = data[f.key];
      if (f.kind === "catalog") {
        const items = Array.isArray(v) ? (v as ChartItem[]) : [];
        for (const i of items) {
          const doses = (i.ingredients ?? []).filter((g) => !g.leftOut).map((g) => `${g.name} ${g.unit === "each" ? `× ${g.dose}` : `${g.dose} ${g.unit}`}`).join(", ");
          const out = (i.ingredients ?? []).filter((g) => g.leftOut).map((g) => `${g.name}${g.leftOutReason ? ` (${g.leftOutReason})` : ""}`);
          lines.push(`${f.label}: ${i.name}${doses ? ` (${doses})` : ""}${out.length ? `; left out: ${out.join(", ")}` : ""}`);
        }
      } else if (f.kind === "vitals") {
        const o = (v ?? {}) as Record<string, string>;
        const parts = (Object.keys(VITAL_RANGES) as VitalKey[]).filter((k) => o[k]).map((k) => `${VITAL_RANGES[k].label} ${o[k]}`);
        if (parts.length) lines.push(`${f.label}: ${parts.join(", ")}`);
      } else if (Array.isArray(v)) {
        if (v.length) lines.push(`${f.label}: ${(v as string[]).join("; ")}`);
      } else if (typeof v === "string" && v.trim()) lines.push(`${f.label}: ${v.trim()}`);
    }
  }
  return lines.join("\n");
}

/** Numbers in `after` that aren't in `before` ("250", "1.5", "14:35"), so a cleanup can't slip one in unnoticed. */
export function numbersAdded(before: string, after: string): string[] {
  const nums = (t: string) => (t.match(/\d+(?:[.,:]\d+)*/g) ?? []).map((n) => n.replace(/,/g, ""));
  const had = new Set(nums(before));
  return [...new Set(nums(after).filter((n) => !had.has(n)))];
}
